import React, { ChangeEvent, useState, useCallback } from "react";
import { FieldError } from "react-hook-form";

import { LabelButton, Label, Required } from "shared/components";
import { Spacer } from "shared/layout";
import { FancyButtonLink } from "shared/components/Link/FancyButtonLink";
import { FlexContainer, Icon, MutedText } from "shared/styles";
import { slideInFromBottom, space, VisuallyHiddenElement } from "shared/styles/constants";
import { IStatus, ITheme, IFileValidation } from "shared/types";

import { FieldWrapperProps } from "../../FieldWrapper";
import { FieldWrapperContainer, Note, ErrorMessage } from "../../styles";
import { StyledFileField, StyledFileUrl, DropZone } from "./styles";

import { copy } from "shared/assets";
import { Loader } from "shared/components/Loader/Loader";
import { Button } from "shared/components/Button";
import { IFileUploadValue } from "../FileUpload";
import { formatFileSize } from "shared/utils";
import { File } from "shared/assets/icons";

const { form } = copy;

export interface FileFieldProps extends FieldWrapperProps {
  value: IFileUploadValue;
  accept?: string;
  isLoading?: boolean;
  required?: boolean;
  error?: FieldError | null;
  handleUpload: (file: File) => Promise<void>;
  handleClear: () => void;
  handleDelete: () => void;
  handleBlur: () => void;
  validation?: IFileValidation;
}

export const FileField = React.forwardRef<HTMLInputElement, FileFieldProps>(
  (
    {
      name,
      value,
      accept,
      label,
      isLoading,
      required,
      note,
      error,
      handleUpload,
      handleClear,
      handleDelete,
      handleBlur,
      validation,
      hideLabel,
    },
    ref
  ) => {
    const [fileName, setFileName] = useState<string>("");
    const [fileErrors, setFileErrors] = useState<string[]>([]);
    const [isDragActive, setIsDragActive] = useState(false);

    const getFileErrors = useCallback(
      (file: File) => {
        const errors = [];

        if (validation) {
          const { types, message: typesMessage } = validation?.type || {};
          const { maxSize, message: sizeMessage } = validation?.fileSize || {};

          if (types) {
            // file type error
            if (file?.type && !types.includes(file.type as any)) {
              typesMessage && errors.push(typesMessage);
            }
          }

          // file size error
          if (maxSize) {
            if (file?.size && file.size > maxSize) {
              sizeMessage && errors.push(`${sizeMessage} ${formatFileSize(maxSize)}`);
            }
          }
        }

        return errors;
      },
      [validation]
    );

    const handleFile = useCallback(
      async (file: File) => {
        if (!file) {
          return null;
        }

        const errors = getFileErrors(file);
        setFileErrors(errors);

        // don't upload if there are file validation errors
        if (errors.length) {
          return;
        }

        try {
          await handleUpload(file);
          const { name } = file;
          setFileName(name);
        } catch (error) {}
      },
      [handleUpload, getFileErrors]
    );

    const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
      const { target } = e;
      const file = target?.files?.[0];
      if (file) {
        await handleFile(file);
      }
    };

    const onClear = () => {
      setFileErrors([]);
      setFileName("");
      handleClear();
    };

    const onDelete = () => {
      setFileErrors([]);
      handleDelete();
    };

    const handleDragEnter = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
      async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        const file = e.dataTransfer.files[0];
        await handleFile(file);
      },
      [handleFile]
    );

    return (
      <FieldWrapperContainer>
        <Spacer size="xs">
          {hideLabel ? (
            <VisuallyHiddenElement as="label" htmlFor={name}>
              {label}
            </VisuallyHiddenElement>
          ) : (
            <Spacer size="xxs" spaceDirection="horizontal">
              <Label>{label}</Label>
              {required && <Required />}
            </Spacer>
          )}

          <div>
            {!value?.originalURL && (
              <StyledFileField>
                {fileName ? (
                  <Spacer size="xxs" spaceDirection="horizontal" style={{ marginTop: space.m }}>
                    <div
                      style={{
                        width: "110px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <MutedText>{fileName}</MutedText>
                    </div>

                    <FancyButtonLink status={IStatus.error} onClick={onClear}>
                      {form.reset}
                    </FancyButtonLink>
                  </Spacer>
                ) : (
                  <DropZone
                    isDragActive={isDragActive}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <LabelButton htmlFor={name} theme={ITheme.tertiary}>
                      <span>{form.chooseFile}</span>
                      <VisuallyHiddenElement>
                        <input
                          ref={ref}
                          id={name}
                          name={name}
                          type="file"
                          accept={accept}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </VisuallyHiddenElement>
                      {isLoading && <Loader size="s" theme={ITheme.tertiary} />}
                    </LabelButton>
                    <MutedText style={{ marginTop: space.xxs }}>{form.dragAndDrop}</MutedText>
                  </DropZone>
                )}
              </StyledFileField>
            )}
          </div>
          {note && <Note>{note}</Note>}
          {fileErrors.map(error => (
            <ErrorMessage data-testid="form_field_error" {...slideInFromBottom}>
              {error}
            </ErrorMessage>
          ))}
          {value && value.error && (
            <ErrorMessage data-testid="form_field_error" {...slideInFromBottom}>
              {copy.form.fileType}
            </ErrorMessage>
          )}

          {/* show delete if original URL provided but no file selected */}
          {value?.originalURL && (
            <FlexContainer>
              <StyledFileUrl href={value.originalURL} target="_blank" rel="noreferrer">
                <Icon as={File} />
                {copy.form.originalFile}
              </StyledFileUrl>
              <Button
                status={IStatus.error}
                onClick={onDelete}
                size="xs"
                data-testid={`delete_file_${name}`}
              >
                {copy.form.deleteFile}
              </Button>
            </FlexContainer>
          )}
        </Spacer>
      </FieldWrapperContainer>
    );
  }
);
